import { useCallback } from 'react';
import type { CustomPanelControls } from '@practium/ui';
import SortByAlphaOutlined from '@mui/icons-material/SortByAlphaOutlined';
import {
	ActionButton,
	Button,
	EmptyState,
	FilterChip,
	ListPageTableSkeleton,
	Pagination,
	PeriodPanel,
	SearchBar,
} from '@practium/ui';

import styles from './ProductRequestsPage.module.css';
import { ProductRequestModals } from '../components/Modals/ProductRequestModals';
import { ProductRequestsTable } from '../components/Tables/ProductRequestsTable';
import { useProductRequests } from '../hooks/useProductRequest';
import { Add } from '@mui/icons-material';

const TABS = [
	{ key: 'pending' as const, label: 'Pendentes' },
	{ key: 'approved' as const, label: 'Aceitos' },
	{ key: 'rejected' as const, label: 'Rejeitados' },
	{ key: 'all' as const, label: 'Todos' },
];

export const ProductRequestsPage = () => {
	const {
		isTableLoading,
		isTableFetching,
		isTableError,
		refetchTable,
		activeTab,
		setActiveTab,
		selectedRequest,
		setSelectedRequest,
		isCreateOpen,
		formData,
		setField,
		formErrors,
		clearError,
		filterGroups,
		filterValues,
		setFilterValues,
		customDateFrom,
		customDateTo,
		handleCustomDateChange,
		searchQuery,
		setSearchQuery,
		sortAsc,
		setSortAsc,
		page,
		setPage,
		paginatedRequests,
		totalPages,
		currentDate,
		userName,
		handleOpenCreate,
		handleCloseCreate,
		handleSubmit,
	} = useProductRequests();

	const renderPeriodPanel = useCallback(
		({ close, activate }: CustomPanelControls) => (
			<PeriodPanel
				requireEndDate
				dateFrom={customDateFrom}
				dateTo={customDateTo}
				onChange={(from, to) => {
					handleCustomDateChange(from, to);
					activate();
				}}
				onClose={close}
			/>
		),
		[customDateFrom, customDateTo, handleCustomDateChange]
	);

	return (
		<div className={styles.page}>
			<div className={styles.contentWrapper}>
				<div className={styles.pageHeader}>
					<div className={styles.pageHeaderLeft}>
						<h1 className={styles.pageTitle}>Solicitações de Produtos</h1>
						<p className={styles.pageSubtitle}>
							Solicite os produtos para serem cadastrados no sistema
						</p>
					</div>
					<div className={styles.pageHeaderRight}>
						<Button variant="primary" icon={<Add className={styles.addIcon} />} className={styles.solicitarBtn} onClick={handleOpenCreate}>
							Solicitar
						</Button>
					</div>
				</div>

				<div className={styles.card}>
					<div className={styles.tabsWrapper}>
						{TABS.map((tab) => (
							<button
								key={tab.key}
								className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
								onClick={() => setActiveTab(tab.key)}
							>
								{tab.label}
							</button>
						))}
					</div>

					<div className={styles.filterRow}>
						<div className={styles.searchWrapper}>
							<SearchBar
								value={searchQuery}
								onChange={setSearchQuery}
								placeholder="Buscar por nome ou fabricante..."
							/>
						</div>
						<button
							type="button"
							className={`${styles.sortBtn} ${sortAsc ? styles.sortBtnActive : ''}`}
							onClick={() => setSortAsc((v) => !v)}
							title="Ordenar em ordem alfabética"
						>
							<SortByAlphaOutlined />
						</button>
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
							customPanels={{ 'periodo:custom': renderPeriodPanel }}
						/>
					</div>

					<div className={styles.tableContent} aria-busy={isTableLoading || isTableFetching}>
						{isTableError ? (
							<div role="alert">
								<EmptyState
									title="Não foi possível carregar as solicitações"
									description="Tente novamente para carregar os dados."
								/>
								<ActionButton text="Tentar novamente" onClick={() => void refetchTable()} />
							</div>
						) : isTableLoading ? (
							<ListPageTableSkeleton label="Carregando solicitações" />
						) : (
							<>
								{isTableFetching && (
									<span role="status" className={styles.refreshStatus}>Atualizando dados…</span>
								)}
								<ProductRequestsTable requests={paginatedRequests} onRowClick={setSelectedRequest} />
							</>
						)}
					</div>

					<div className={styles.paginationRow}>
						{!isTableLoading && !isTableError && (
							<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
						)}
					</div>
				</div>

				<ProductRequestModals
					selectedRequest={selectedRequest}
					onCloseDetail={() => setSelectedRequest(null)}
					isCreateOpen={isCreateOpen}
					onCloseCreate={handleCloseCreate}
					onSubmit={handleSubmit}
					formData={formData}
					onFieldChange={setField}
					formErrors={formErrors}
					onClearError={clearError}
					currentDate={currentDate}
					userName={userName}
				/>
			</div>
		</div>
	);
};
